<template>
  <Dialog v-model:open="isOpen">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-[100] bg-black/80" />
      <!-- Fullscreen modal using raw reka-ui DialogContent (no default close button) -->
      <DialogContent 
        class="fixed inset-0 z-[100] bg-background flex flex-col overflow-hidden outline-none"
      >
        <!-- MOBILE VIEW: Drawer-based layout -->
        <MobileEditorDrawer
          v-if="isMobileView"
          ref="mobileDrawerRef"
          :display-name="currentDisplay?.name ?? 'Display'"
          :is-saving="isSaving"
          @close="isOpen = false"
          @save="handleSave"
        >
          <template #canvas>
            <div 
              class="w-full h-full relative"
              :class="{
                'bg-blue-100 border-2 border-blue-500 border-dashed': isDragOverPanel || isTauriDragging
              }"
              @dragover.prevent="isDragOverPanel = true"
              @dragleave="isDragOverPanel = false"
              @drop.prevent="handlePanelDrop"
            >
              <CanvasEditor
                v-if="currentDisplay"
                :display="currentDisplay"
                :layers="layers"
                :selected-layer-id="selectedLayerId"
                @select-layer="handleMobileLayerSelect"
                @update-layer="handleUpdateLayer"
                @delete-layer="handleDeleteLayer"
              />
            </div>
          </template>

          <template #layers>
            <LayerList
              :layers="layers"
              :selected-layer-id="selectedLayerId"
              @select-layer="handleMobileLayerSelect"
              @reorder-layers="handleReorderLayers"
              @delete-layer="handleDeleteLayer"
              @update-layer="handleUpdateLayer"
            />
          </template>

          <template #props>
            <LayerControls
              v-if="selectedLayer"
              :layer="selectedLayer"
              :parentLayer="parentLayer"
              @update-layer="handleUpdateLayer"
            />
            <div v-else class="text-center text-muted-foreground py-8 text-sm">
              Select a layer to edit its properties
            </div>
          </template>

          <template #library>
            <ImageLibrary
              v-if="sceneDisplayConfigId"
              :scene-display-config-id="sceneDisplayConfigId"
              @add-image="handleAddImageLayerMobile"
              @add-youtube="handleAddYoutubeLayerMobile"
            />
            <div v-else class="py-8 text-center text-muted-foreground text-sm">
              Loading library...
            </div>
          </template>
        </MobileEditorDrawer>

        <!-- DESKTOP VIEW: Original layout -->
        <template v-else>
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b shrink-0 bg-background">
          <div>
            <h2 class="text-base font-semibold">Display Editor - {{ currentDisplay?.name ?? 'Display' }}</h2>
            <p class="text-xs text-muted-foreground">Edit the visual content for this display</p>
          </div>
          <button
            @click="isOpen = false"
            class="p-1 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
          >
            <X class="w-5 h-5" />
            <span class="sr-only">Close</span>
          </button>
        </div>

        <div class="flex-1 flex flex-row min-h-0 overflow-hidden">

          <!-- Left Panel: Layer List -->
          <div class="flex w-52 lg:w-64 xl:w-72 border-r bg-muted/30 p-2 lg:p-3 flex-col gap-4 shrink-0 overflow-hidden">
            <div class="flex-1 min-h-0 overflow-y-auto">
              <LayerList
                :layers="layers"
                :selected-layer-id="selectedLayerId"
                @select-layer="selectedLayerId = $event"
                @reorder-layers="handleReorderLayers"
                @delete-layer="handleDeleteLayer"
                @update-layer="handleUpdateLayer"
              />
            </div>
            
            <!-- Parent Scene Selector -->
            <div class="shrink-0 pt-3 border-t">
              <ParentSceneSelector :scene-id="sceneId" />
            </div>
          </div>

          <!-- Center Panel: Canvas Editor -->
          <div 
            class="flex-1 flex flex-col min-w-0 min-h-0 p-4"
            @dragover.prevent="isDragOverPanel = true"
            @dragleave="isDragOverPanel = false"
            @drop.prevent="handlePanelDrop"
          >
            <div 
              class="flex-1 relative bg-muted/20 overflow-hidden rounded-lg"
            :class="{
              'bg-blue-100 border-2 border-blue-500 border-dashed': isDragOverPanel || isTauriDragging
            }"
          >
            <!-- Tauri drag overlay -->
            <div 
              v-if="isTauriDragging" 
              class="absolute inset-0 z-[200] bg-blue-500/10 border-4 border-blue-500 border-dashed flex items-center justify-center pointer-events-none"
            >
              <div class="bg-white/95 px-6 py-4 rounded-lg shadow-xl">
                <p class="text-lg font-semibold text-blue-600">Drop files here</p>
                <p class="text-sm text-gray-600 mt-1">Images will be added as layers</p>
              </div>
            </div>
            
            <CanvasEditor
              v-if="currentDisplay"
              :display="currentDisplay"
              :layers="layers"
              :selected-layer-id="selectedLayerId"
              @select-layer="selectedLayerId = $event"
              @update-layer="handleUpdateLayer"
              @delete-layer="handleDeleteLayer"
            />
            <div v-else class="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
              Loading display...
            </div>
          </div>
        </div>

          <!-- Right Panel: Controls and Library -->
          <div class="flex w-56 lg:w-72 xl:w-96 border-l bg-muted/30 p-3 lg:p-4 flex-col shrink-0 overflow-visible">
            <Tabs default-value="controls" class="flex-1 flex flex-col min-h-0">
              <TabsList class="grid w-full grid-cols-2 shrink-0">
                <TabsTrigger value="controls" class="text-xs lg:text-sm">Controls</TabsTrigger>
                <TabsTrigger value="library" class="text-xs lg:text-sm">Library</TabsTrigger>
              </TabsList>

              <TabsContent value="controls" class="mt-2 lg:mt-4 flex-1 overflow-y-auto overflow-x-visible pr-1">
                <LayerControls
                  v-if="selectedLayer"
                  :layer="selectedLayer"
                  :parentLayer="parentLayer"
                  @update-layer="handleUpdateLayer"
                />
                <div v-else class="text-center text-muted-foreground py-4 text-xs lg:text-sm">
                  Select a layer to edit
                </div>
              </TabsContent>

              <TabsContent value="library" class="mt-2 lg:mt-4 flex-1 overflow-y-auto">
                <ImageLibrary
                  v-if="sceneDisplayConfigId"
                  :scene-display-config-id="sceneDisplayConfigId"
                  @add-image="handleAddImageLayer"
                  @add-youtube="handleAddYoutubeLayer"
                />
                <div v-else class="py-4 text-center text-muted-foreground text-xs">
                  Loading...
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        </template>
      </DialogContent>
    </DialogPortal>
  </Dialog>
</template>

<script setup lang="ts">
import { logger } from '@/lib/logger'
import { getDataProvider } from '@/lib/dataProvider'
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { cloneDisplayLayer, displayLayerToUpdatePayload, normalizeDisplayLayer, useSceneDisplayConfigsStore } from '@/stores/sceneDisplayConfigs'
import { useDisplayLayersStore } from '@/stores/displayLayers'
import { useImageLibraryStore } from '@/stores/imageLibrary'
import { useTauriFileDrop } from '@/composables/useTauriFileDrop'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { DialogPortal, DialogOverlay, DialogContent } from 'reka-ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { X } from 'lucide-vue-next'
import type { Display } from '@/stores/displays'
import type { DisplayLayer, UpdateDisplayLayerData } from '@/stores/sceneDisplayConfigs'
import { regeneratePreviewsForSceneTree } from '@/services/scenePreviewService'
import LayerList from './editor/LayerList.vue'
import CanvasEditor from './editor/CanvasEditor.vue'
import LayerControls from './editor/LayerControls.vue'
import ImageLibrary from './editor/ImageLibrary.vue'
import MobileEditorDrawer from './editor/MobileEditorDrawer.vue'
import ParentSceneSelector from './editor/ParentSceneSelector.vue'

interface Props {
  sceneId: string
  display: Display
  open: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:open': [value: boolean]
  'display-updated': [display: Display]
}>()

const configsStore = useSceneDisplayConfigsStore()
const layersStore = useDisplayLayersStore()
const imageLibraryStore = useImageLibraryStore()

// Setup Tauri file drop handling for external file drops
const { isDragging: isTauriDragging } = useTauriFileDrop(
  async (files: File[]) => {
    if (!sceneDisplayConfigId.value) return
    
    logger.info('[DisplayEditor] Tauri file drop received:', files.length, 'files')
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        logger.warn('[DisplayEditor] Skipping non-image file:', file.name)
        continue
      }

      try {
        logger.info('[DisplayEditor] Uploading dropped image:', file.name)
        const uploadedImage = await imageLibraryStore.uploadImage(file)
        const newLayer = await layersStore.createImageLayer(sceneDisplayConfigId.value, uploadedImage.id)
        const normalized: DisplayLayer = normalizeDisplayLayer(newLayer)
        localLayers.value = [...localLayers.value, cloneDisplayLayer(normalized)]
        selectedLayerId.value = newLayer.id
        logger.success('[DisplayEditor] Successfully added layer from dropped file')
      } catch (error) {
        logger.error('[DisplayEditor] Failed to upload dropped image:', error)
      }
    }
  },
  {
    acceptedTypes: ['image/*'],
    debug: true
  }
)

// Reactive state
const isOpen = computed({
  get: () => props.open,
  set: (value) => emit('update:open', value)
})

const selectedLayerId = ref<string | null>(null)
const isSaving = ref(false)
const isDragOverPanel = ref(false)
const isReordering = ref(false)
const sceneDisplayConfigId = ref<string | null>(null)
const loadedDisplayId = ref<string | null>(null)

// Mobile responsiveness
const mobileTab = ref<'canvas' | 'layers' | 'library'>('canvas')
const isMobileView = ref(false)

// Check viewport size for responsive behavior
const updateMobileView = () => {
  isMobileView.value = window.innerWidth < 768 // md breakpoint
}

// Set up resize listener
if (typeof window !== 'undefined') {
  updateMobileView()
  window.addEventListener('resize', updateMobileView)
}

const currentDisplay = ref<Display | null>(props.display ? { ...props.display } : null)

watch(
  () => props.display,
  (newDisplay) => {
    currentDisplay.value = newDisplay ? { ...newDisplay } : null
  },
  { immediate: true }
)

const localLayers = ref<DisplayLayer[]>([])

// Store parent layers for inheritance comparison (before overrides)
const parentLayersMap = ref<Map<string, DisplayLayer>>(new Map())
// This prevents overwriting inheritance metadata
// const storeLayers = computed<DisplayLayer[]>(() => {
//   if (!sceneDisplayConfigId.value) return []
//   return layersStore.getLayersForDisplay(sceneDisplayConfigId.value)
// })

// watch(storeLayers, (newLayers: DisplayLayer[]) => {
//   localLayers.value = newLayers.map(cloneDisplayLayer)
// }, { immediate: true, deep: true })

const layers = computed<DisplayLayer[]>(() => localLayers.value)

interface LayerUpdateOptions {
  commit?: boolean
  deferCommit?: boolean
}

const pendingLayerUpdates = new Map<string, UpdateDisplayLayerData>()
const layerUpdateTimers = new Map<string, ReturnType<typeof setTimeout>>()

function clearLayerTimer(layerId: string) {
  const timer = layerUpdateTimers.get(layerId)
  if (timer) {
    clearTimeout(timer)
    layerUpdateTimers.delete(layerId)
  }
}

function resetPendingUpdates() {
  pendingLayerUpdates.clear()
  for (const timer of layerUpdateTimers.values()) {
    clearTimeout(timer)
  }
  layerUpdateTimers.clear()
}

async function commitLayerUpdate(layerId: string) {
  const updates = pendingLayerUpdates.get(layerId)
  if (!updates) return

  clearLayerTimer(layerId)

  try {
    // Find the layer to check its inheritance status
    const targetLayer = localLayers.value.find(l => l.id === layerId)
    
    logger.info('Committing layer update:', {
      layerId,
      updates,
      is_inherited: targetLayer?.is_inherited,
      inherited_from_scene_id: targetLayer?.inherited_from_scene_id,
      current_scene_id: props.sceneId,
      scene_display_config_id: sceneDisplayConfigId.value,
      display_id: props.display.id,
      targetLayer_keys: targetLayer ? Object.keys(targetLayer) : []
    })
    
    console.log('Full targetLayer object:', targetLayer)
    
    const committedLayer = await layersStore.updateLayer(
      layerId, 
      updates, 
      sceneDisplayConfigId.value || undefined,
      props.sceneId,
      props.display.id,
      targetLayer  // Pass the layer with inheritance metadata
    )
    
    // Refresh layers to reflect changes
    await loadLayers()
    
    // Keep selection on the new override layer if one was created
    if (committedLayer) {
      selectedLayerId.value = committedLayer.id
    }
  } catch (error) {
    logger.error('Failed to update layer:', error)
  } finally {
    pendingLayerUpdates.delete(layerId)
  }
}

function scheduleLayerCommit(layerId: string, delay = 250) {
  clearLayerTimer(layerId)
  const timer = setTimeout(() => {
    layerUpdateTimers.delete(layerId)
    void commitLayerUpdate(layerId)
  }, delay)
  layerUpdateTimers.set(layerId, timer)
}

// Computed
const selectedLayer = computed(() =>
  layers.value.find(layer => layer.id === selectedLayerId.value) || null
)

// Fetch parent layer data for inheritance comparison
const parentLayer = computed<DisplayLayer | undefined>(() => {
  if (!selectedLayer.value?.is_inherited || !selectedLayer.value.id) {
    return undefined
  }
  
  // Get the original parent layer (before overrides) from our map
  return parentLayersMap.value.get(selectedLayer.value.id)
})

const handleDisplayMetaUpdated = (updatedDisplay: Display) => {
  currentDisplay.value = { ...updatedDisplay }
  emit('display-updated', updatedDisplay)
}

const loadLayers = async () => {
  try {
    // Ensure base configs are loaded
    await configsStore.fetchConfigsForScene(props.sceneId)

    const display = currentDisplay.value
    if (!display) return

    let directConfig = configsStore.getConfigForSceneAndDisplay(props.sceneId, display.id)

    if (!directConfig) {
      const result = await configsStore.createOrUpdateConfig({
        scene_id: props.sceneId,
        display_id: display.id
      })

      if (!result.success || !result.data) {
        logger.error('Failed to create config:', result.error)
        return
      }

      directConfig = result.data
    }

    if (!directConfig) return

    sceneDisplayConfigId.value = directConfig.id
    const fetchedLayers = await layersStore.fetchLayersWithInheritance(directConfig.id, props.sceneId, props.display.id)
    
    // Build parent layers map for inheritance comparison
    // For inherited layers, fetch their original parent data before overrides
    const newParentMap = new Map<string, DisplayLayer>()
    
    for (const layer of fetchedLayers) {
      if (layer.is_inherited && layer.id) {
        // Fetch the original layer data directly from database
        // This gives us the parent values before any overrides
        const provider = getDataProvider()
        const originalLayerData = await provider.getById<any>('display_layers', layer.id)
        
        if (originalLayerData) {
          // Convert database format to DisplayLayer format manually
          const parentLayer: DisplayLayer = {
            ...originalLayerData,
            type: originalLayerData.layer_type,
            content: originalLayerData.content_source,
            x: originalLayerData.position_x,
            y: originalLayerData.position_y,
            zIndex: originalLayerData.z_index,
            crop: originalLayerData.crop_settings,
            objectFit: originalLayerData.object_fit,
          } as DisplayLayer
          
          newParentMap.set(layer.id, parentLayer)
        }
      }
    }
    
    parentLayersMap.value = newParentMap
    
    // Apply pending updates to fetched layers to prevent UI jumping
    localLayers.value = fetchedLayers.map(layer => {
      const cloned = cloneDisplayLayer(layer)
      const pending = pendingLayerUpdates.get(layer.id)
      
      if (pending) {
        if (pending.position_x !== undefined) cloned.x = pending.position_x
        if (pending.position_y !== undefined) cloned.y = pending.position_y
        if (pending.width !== undefined) cloned.width = pending.width
        if (pending.height !== undefined) cloned.height = pending.height
        if (pending.z_index !== undefined) cloned.zIndex = pending.z_index
        if (pending.visible !== undefined) cloned.visible = pending.visible
        if (pending.crop_settings) cloned.crop = pending.crop_settings
        if (pending.effects) cloned.effects = { ...cloned.effects, ...pending.effects }
      }
      
      return cloned
    })
    
    loadedDisplayId.value = props.display.id
  } catch (error) {
    logger.error('Failed to load layers:', error)
  }
}

watch(
  () => currentDisplay.value?.id,
  async (newId, oldId) => {
    if (!props.open) return
    if (newId && newId !== oldId) {
      await loadLayers()
    }
  }
)

// Load layers when modal opens
watch(() => props.open, async (newOpen) => {
  if (newOpen) {
    selectedLayerId.value = null
    
    // Only clear layers if we're opening a different display
    if (loadedDisplayId.value !== props.display.id) {
      localLayers.value = []
      loadedDisplayId.value = null
    }
    
    await loadLayers()
  } else {
    selectedLayerId.value = null
    isDragOverPanel.value = false
    // Don't clear localLayers here to allow caching
    // localLayers.value = []
    resetPendingUpdates()
  }
}, { immediate: true })

const handleReorderLayers = async (newLayers: DisplayLayer[]) => {
  if (!sceneDisplayConfigId.value || isReordering.value) return

  try {
    isReordering.value = true
    
    // Clear all pending layer updates to prevent race conditions
    resetPendingUpdates()
    
    logger.info('[DisplayEditor] Reordering layers:', {
      newOrder: newLayers.map(l => ({
        id: l.id,
        name: l.name,
        zIndex: l.zIndex,
        is_inherited: l.is_inherited
      }))
    })
    
    // Batch all z-index updates together
    const updates: PromiseLike<any>[] = []
    
    for (const layer of newLayers) {
      if (layer.is_inherited) {
        // For inherited layers, upsert override with just z_index
        logger.info('[DisplayEditor] Creating z_index override:', {
          layer_id: layer.id,
          layer_name: layer.name,
          z_index: layer.zIndex
        })
        
        // Update cache immediately
        layersStore.updateOverrideZIndexInCache(sceneDisplayConfigId.value, layer.id, layer.zIndex)
        
        updates.push(
          (async () => {
            const provider = getDataProvider()
            const result = await provider.upsert('display_layer_overrides', {
              scene_display_config_id: sceneDisplayConfigId.value,
              parent_layer_id: layer.id,
              z_index: layer.zIndex
            }, 'scene_display_config_id,parent_layer_id')
            if (!result.success) throw new Error(result.error)
            logger.info('[DisplayEditor] Override upsert result:', result)
            return result
          })()
        )
      } else {
        // For own layers, update z_index directly
        logger.info('[DisplayEditor] Updating z_index directly:', {
          layer_id: layer.id,
          layer_name: layer.name,
          z_index: layer.zIndex
        })
        
        // Update cache immediately
        layersStore.updateLayerZIndexInCache(layer.id, layer.zIndex)
        
        updates.push(
          (async () => {
            const provider = getDataProvider()
            const result = await provider.update('display_layers', layer.id, { z_index: layer.zIndex })
            if (!result.success) throw new Error(result.error)
            logger.info('[DisplayEditor] Layer update result:', result)
            return result
          })()
        )
      }
    }
    
    // Wait for all updates to complete
    logger.info('[DisplayEditor] Waiting for all updates to complete...')
    await Promise.all(updates)
    logger.info('[DisplayEditor] All updates complete, fetching refreshed layers...')
    
    // Single refresh after all updates are done
    const refreshedLayers = await layersStore.fetchLayersWithInheritance(
      sceneDisplayConfigId.value,
      props.sceneId,
      props.display.id
    )
    
    logger.info('[DisplayEditor] Refreshed layers:', {
      layers: refreshedLayers.map(l => ({
        id: l.id,
        name: l.name,
        zIndex: l.zIndex,
        z_index: l.z_index,
        is_inherited: l.is_inherited,
        has_overrides: l.has_overrides
      }))
    })
    
    localLayers.value = refreshedLayers.map(cloneDisplayLayer)
  } catch (error) {
    logger.error('Failed to reorder layers:', error)
  } finally {
    isReordering.value = false
  }
}

const handleDeleteLayer = async (layerId: string) => {
  if (!layerId) return

  console.log('[DisplayEditorModal] 🗑️ handleDeleteLayer START', {
    layerId,
    sceneDisplayConfigId: sceneDisplayConfigId.value,
    sceneId: props.sceneId,
    displayId: props.display.id,
    localLayersBefore: localLayers.value.map(l => ({ id: l.id, name: l.name, is_inherited: l.is_inherited }))
  })

  try {
    await layersStore.deleteLayer(
      layerId, 
      sceneDisplayConfigId.value || undefined,
      props.sceneId,
      props.display.id
    )
    pendingLayerUpdates.delete(layerId)
    clearLayerTimer(layerId)
    
    console.log('[DisplayEditorModal] 🔍 Fetching layers with inheritance after delete')
    // Refresh local layers using fetchLayersWithInheritance to get computed layers
    const refreshedLayers = await layersStore.fetchLayersWithInheritance(
      sceneDisplayConfigId.value || '',
      props.sceneId,
      props.display.id
    )
    console.log('[DisplayEditorModal] 📦 Refreshed layers:', {
      count: refreshedLayers.length,
      layers: refreshedLayers.map(l => ({ id: l.id, name: l.name, is_inherited: l.is_inherited, visible: l.visible }))
    })
    
    localLayers.value = refreshedLayers.map(cloneDisplayLayer)
    console.log('[DisplayEditorModal] ✅ Local layers updated:', {
      count: localLayers.value.length,
      layers: localLayers.value.map(l => ({ id: l.id, name: l.name, is_inherited: l.is_inherited }))
    })
    
    if (selectedLayerId.value === layerId) {
      selectedLayerId.value = null
    }
  } catch (error) {
    logger.error('Failed to delete layer:', error)
  }
}

const handleUpdateLayer = async (updatedLayer: DisplayLayer, options: LayerUpdateOptions = {}) => {
  if (!updatedLayer || !updatedLayer.id || isReordering.value) return

  selectedLayerId.value = updatedLayer.id

  const normalizedLayer: DisplayLayer = normalizeDisplayLayer(updatedLayer)
  localLayers.value = localLayers.value.map(layer =>
    layer.id === normalizedLayer.id ? cloneDisplayLayer(normalizedLayer) : layer
  )

  const updates = displayLayerToUpdatePayload(normalizedLayer)
  pendingLayerUpdates.set(normalizedLayer.id, updates)

  if (options.deferCommit) {
    clearLayerTimer(normalizedLayer.id)
    return
  }

  if (options.commit === true) {
    await commitLayerUpdate(normalizedLayer.id)
    return
  }

  scheduleLayerCommit(normalizedLayer.id)
}

const handleAddImageLayer = async (imageId: string) => {
  console.log('🎯 handleAddImageLayer called with imageId:', imageId)
  if (!sceneDisplayConfigId.value) {
    console.error('❌ No sceneDisplayConfigId!')
    return
  }

  try {
    // Check if this is a video file
    const image = imageLibraryStore.images.find(img => img.id === imageId)
    const isVideo = image?.mime_type?.startsWith('video/')

    console.log('📞 Calling layersStore.create' + (isVideo ? 'Video' : 'Image') + 'Layer...')
    const newLayer = isVideo
      ? await layersStore.createVideoLayer(sceneDisplayConfigId.value, imageId)
      : await layersStore.createImageLayer(sceneDisplayConfigId.value, imageId)
    console.log('✨ New layer received:', newLayer)
    const normalized: DisplayLayer = normalizeDisplayLayer(newLayer)
    localLayers.value = [...localLayers.value, cloneDisplayLayer(normalized)]
    selectedLayerId.value = newLayer.id
  } catch (error) {
    logger.error('Failed to add layer:', error)
  }
}

const handleAddYoutubeLayer = async (youtubeUrl: string) => {
  if (!sceneDisplayConfigId.value) return

  try {
    const newLayer = await layersStore.createYouTubeLayer(sceneDisplayConfigId.value, youtubeUrl)
  const normalized: DisplayLayer = normalizeDisplayLayer(newLayer)
  localLayers.value = [...localLayers.value, cloneDisplayLayer(normalized)]
    selectedLayerId.value = newLayer.id
  } catch (error) {
    logger.error('Failed to add YouTube layer:', error)
  }
}

// Mobile-specific handlers
const mobileDrawerRef = ref<InstanceType<typeof MobileEditorDrawer> | null>(null)

const handleMobileLayerSelect = (layerId: string) => {
  selectedLayerId.value = layerId
  // Switch to props tab when a layer is selected
  mobileDrawerRef.value?.switchToProps()
}

const handleAddImageLayerMobile = async (imageId: string) => {
  await handleAddImageLayer(imageId)
  // Switch to layers tab to see the new layer
  if (mobileDrawerRef.value) {
    mobileDrawerRef.value.activeTab = 'layers'
  }
}

const handleAddYoutubeLayerMobile = async (youtubeUrl: string) => {
  await handleAddYoutubeLayer(youtubeUrl)
  // Switch to layers tab to see the new layer
  if (mobileDrawerRef.value) {
    mobileDrawerRef.value.activeTab = 'layers'
  }
}

const handlePanelDrop = async (e: DragEvent) => {
  // This handles HTML5 drag-and-drop (internal app drags and web browser file drags)
  // External file drops in Tauri are handled by useTauriFileDrop composable
  isDragOverPanel.value = false

  const dataTransfer = e.dataTransfer
  if (!dataTransfer) return

  const files = dataTransfer.files && dataTransfer.files.length > 0
    ? Array.from(dataTransfer.files)
    : []

  if (files.length > 0) {
    if (!sceneDisplayConfigId.value) return

    for (const file of files) {
      if (!file.type.startsWith('image/')) continue

      try {
        const uploadedImage = await imageLibraryStore.uploadImage(file)
        const newLayer = await layersStore.createImageLayer(sceneDisplayConfigId.value, uploadedImage.id)
  const normalized: DisplayLayer = normalizeDisplayLayer(newLayer)
  localLayers.value = [...localLayers.value, cloneDisplayLayer(normalized)]
        selectedLayerId.value = newLayer.id
      } catch (error) {
        logger.error('Failed to upload dropped image:', error)
      }
    }

    return
  }

  const data = dataTransfer.getData('application/json')
  if (!data) return

  try {
    const dropData = JSON.parse(data)
    
    if (dropData.type === 'image' && dropData.imageId) {
      await handleAddImageLayer(dropData.imageId)
    } else if (dropData.type === 'youtube' && dropData.url) {
      await handleAddYoutubeLayer(dropData.url)
    }
  } catch (error) {
    logger.error('Failed to handle panel drop:', error)
  }
}

const flushPendingLayerUpdates = async () => {
  if (pendingLayerUpdates.size === 0) return

  for (const timer of layerUpdateTimers.values()) {
    clearTimeout(timer)
  }
  layerUpdateTimers.clear()

  const pendingIds = Array.from(pendingLayerUpdates.keys())
  for (const layerId of pendingIds) {
    await commitLayerUpdate(layerId)
  }
}

const handleSave = async () => {
  isSaving.value = true
  try {
    await flushPendingLayerUpdates()
    await configsStore.fetchResolvedConfigsForScene(props.sceneId)
    
    // Preview regeneration is handled by the isOpen watcher when modal closes
    isOpen.value = false
  } catch (error) {
    logger.error('Failed to save display config:', error)
  } finally {
    isSaving.value = false
  }
}

const handleCancel = () => {
  isOpen.value = false
  resetPendingUpdates()
}

// Regenerate preview when modal closes (since changes are committed immediately)
watch(isOpen, (open, wasOpen) => {
  if (wasOpen && !open) {
    // Modal just closed - regenerate preview in background
    logger.info('[DisplayEditor] Modal closed, regenerating preview')
    regeneratePreviewsForSceneTree(props.sceneId, props.display.id, props.display)
      .catch(err => logger.warn('Failed to regenerate previews on close:', err))
  }
})

onBeforeUnmount(() => {
  resetPendingUpdates()
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateMobileView)
  }
})
</script>

<style scoped>
/* Ensure fullscreen on mobile with dynamic viewport height */
:deep([data-radix-dialog-content]) {
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
}
</style>